import React from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { unwrapResult } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { useReduxDispatch } from '../../../app/store';
import { ArcButton } from '../../../common/components/ArcButton';
import { selectEvChargerData, setIsLoaded, startCharging, stopCharging } from './evChargerSlice';

export const ChargingControl = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useReduxDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const { isUpdating, siteDeviceId, status } = useSelector(selectEvChargerData);

  const showErrorToast = (error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        category: 'feature',
        feature: 'ev-charger',
        component: 'ChargingControl',
        method: 'handleCharging',
      },
      extra: { siteDeviceId, status },
    });
    toast({
      title: t('Common.something went wrong'),
      description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
      status: 'error',
      isClosable: true,
    });
  };

  async function handleStop() {
    try {
      unwrapResult(await dispatch(stopCharging(siteDeviceId)));
      dispatch(setIsLoaded(false)); // This will trigger a refetch of the EV charger state.
    } catch (error) {
      showErrorToast(error);
    }
  }

  async function handleStart() {
    try {
      unwrapResult(await dispatch(startCharging(siteDeviceId)));
      dispatch(setIsLoaded(false)); // This will trigger a refetch of the EV charger state.
    } catch (error) {
      showErrorToast(error);
    }
  }

  return (
    <>
      {status === 'Charging' && (
        <>
          <ArcButton
            data-testid="ev-charging-control-button"
            onClick={onOpen}
            isLoading={isUpdating}
            mt={8}
            w={'80%'}
            arcColor="#42B4E6"
          >
            {t('EVSE.stop charging')}
          </ArcButton>

          <AlertDialogModal
            {...{ isOpen, onClose }}
            header={t('EVSE.stop charging?')}
            subHeader={t('EVSE.stop charging confirmation')}
            confirmButtonName={t('EVSE.stop charging')}
            onConfirm={handleStop}
            dialogContentTestId="stop-charging-dialog"
          />
        </>
      )}
      {status === 'SuspendedEVSE' && (
        <ArcButton
          data-testid="ev-charging-control-button"
          onClick={handleStart}
          isLoading={isUpdating}
          mt={8}
          w={'80%'}
          arcColor="#42B4E6"
        >
          {t('EVSE.start charging')}
        </ArcButton>
      )}
    </>
  );
};
